import { Link } from 'react-router-dom';

export function PrivacyPolicy() {
  return (
    <div className="min-h-dvh bg-surface">
      <nav className="fixed top-0 left-0 right-0 bg-white border-b border-border px-8 py-4 flex items-center justify-between z-[100]">
        <Link
          to="/landing"
          className="flex items-center gap-2 no-underline"
        >
          <img
            src="/logo.svg"
            alt="Yaruta"
            className="h-8 w-8"
          />
          <span className="text-xl font-bold text-primary">Yaruta</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/terms"
            className="text-sm text-muted-foreground no-underline hover:text-foreground"
          >
            Terms
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground no-underline hover:text-foreground"
          >
            Sign In
          </Link>
        </div>
      </nav>

      <main className="max-w-[720px] mx-auto px-6 pt-24 pb-16">
        <h1 className="text-4xl font-bold text-foreground mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-10">
          Effective Date: September 2026
        </p>

        <p className="text-base text-foreground leading-relaxed mb-8">
          Yaruta ("we," "our," or "us") helps families organize school, sports,
          camp, and child-related logistics by transforming relevant email
          communications into clear summaries, tasks, and calendar-ready events.
        </p>
        <p className="text-sm text-muted-foreground mb-10">
          Yaruta services may be accessible through yaruta.app, getyaruta.com,
          and related product experiences.
        </p>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            1. Information We Collect
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            When you use Yaruta, we may collect:
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed">
            <li>
              Account information such as your name, email address, and login
              credentials
            </li>
            <li>Phone numbers you provide to receive text message reminders</li>
            <li>
              SMS/RCS consent and preference records, including when you opted
              in or out, the consent language presented to you, and your
              selected send time
            </li>
            <li>
              SMS/RCS delivery and interaction information, such as message
              status and STOP or HELP requests
            </li>
            <li>
              Connected email communications and metadata from Gmail or Outlook
              inboxes that you explicitly authorize Yaruta to access
            </li>
            <li>
              Child- and family-related email communications, including message
              content, sender information, timestamps, attachment metadata, and
              extracted event details from communications you authorize
            </li>
            <li>
              Images referenced by those communications. School announcements
              often place the date, time, and location only inside a flyer
              picture, so to read them Yaruta retrieves that image from
              whichever third-party service the sender used to host it (for
              example a newsletter provider&rsquo;s content network). Yaruta
              requests only images the message itself points to, sends no
              account information or cookies with the request, and processes the
              image solely to extract event details for your review
            </li>
            <li>
              AI-generated outputs, including summaries, extracted tasks,
              reminders, and calendar events
            </li>
            <li>
              Household information, such as collaborator profiles, parent
              roles, and child names, grades, and school information (optional)
            </li>
            <li>
              Usage data, including feature interactions, app diagnostics, and
              device/browser information
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            2. How We Use Your Information
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            We use your information to:
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed">
            <li>
              Summarize school, sports, camp, and family-related email
              communications
            </li>
            <li>
              Extract tasks, deadlines, forms, reminders, and calendar events
            </li>
            <li>
              Help parents review and approve updates before adding them to
              their household schedule
            </li>
            <li>Sync approved items into the Yaruta household calendar</li>
            <li>
              Notify you about pending reviews and updates through the channels
              you enable
            </li>
            <li>
              Send recurring service-related SMS and/or RCS reminders and
              digests about your family's schedules, events, tasks, reminders,
              and items requiring review when you explicitly opt in
            </li>
            <li>
              Manage your SMS/RCS preferences, send time, delivery status, HELP
              requests, and opt-out requests
            </li>
            <li>
              Maintain records of SMS/RCS consent as required for compliance and
              service administration
            </li>
            <li>
              Improve extraction accuracy, product reliability, and user
              experience
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            3. Google API and Gmail Access
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-4">
            If you connect Gmail, Yaruta accesses only the email communications
            and metadata necessary to provide the features you enable, such as
            summarizing school communications, extracting deadlines, generating
            reminders, and helping build your household schedule.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Yaruta only processes communications relevant to the workflows you
            configure, such as:
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed mb-4">
            <li>Approved senders</li>
            <li>School domains</li>
            <li>Manually forwarded communications</li>
            <li>Other explicitly connected family logistics sources</li>
          </ul>
          <p className="text-base text-foreground leading-relaxed mb-4">
            Yaruta's use and transfer of information received from Google APIs
            will adhere to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:text-primary-hover"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed mb-4">
            <li>We do not sell Google user data.</li>
            <li>We do not use Gmail data for advertising.</li>
            <li>
              We only use Google-connected email communications to provide the
              features clearly described in this policy.
            </li>
          </ul>
          <p className="text-base text-foreground leading-relaxed">
            <span className="font-semibold">
              Sender suggestions during onboarding.
            </span>{' '}
            When you connect Gmail, we perform a one-time scan of messages from
            the last 180 days (up to the 500 most recent), reading the{' '}
            <code>From</code> and <code>Subject</code> headers and the beginning
            of each message body (approximately the first 200 characters) to
            identify and rank likely senders for your allowlist. We do not store
            message bodies or attachments from this scan, and the scanned data
            is discarded once your suggestions are generated. Only the senders
            and domains you explicitly confirm are saved to your account.
          </p>
        </section>

        <section className="mb-10">
          {/* Twilio RCS sender review (YAR-462). Mirrored in
              settings/legal/PrivacyPolicyBody.tsx. */}
          <h2 className="text-xl font-semibold text-foreground mb-4">
            4. SMS/RCS Communications
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-4">
            If you explicitly opt in, Yaruta may send recurring service-related
            SMS and/or RCS messages about your family's schedules, events,
            tasks, reminders, daily digests, and items requiring review to the
            phone number you provide. SMS/RCS consent is optional, is collected
            separately from account creation, and is not required to use Yaruta.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-4">
            Message frequency varies. Message and data rates may apply. Reply
            STOP to cancel and opt out of future recurring messages. Reply HELP
            for help or contact{' '}
            <a
              href="mailto:hello@yaruta.app"
              className="text-primary underline hover:text-primary-hover"
            >
              hello@yaruta.app
            </a>
            . You can also turn off text message reminders in Yaruta's
            notification settings. Opting out of SMS/RCS does not prevent you
            from using other Yaruta features.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-4">
            You will not be held responsible for failing to opt out of Yaruta
            messages solely because your mobile phone number changes or is
            deactivated.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-4">
            We use service providers, including Twilio, to deliver and manage
            SMS/RCS messages. These providers receive information necessary to
            provide the messaging service, such as your phone number, message
            content, delivery information, and opt-out or help requests.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-4">
            We do not sell your phone number or SMS/RCS consent information. We
            do not share mobile information or SMS/RCS consent data with third
            parties or affiliates for their own marketing or promotional
            purposes. This does not include service providers acting on our
            behalf to deliver the messaging service.
          </p>
          <p className="text-base text-foreground leading-relaxed">
            For the terms governing Yaruta's messaging program, please review
            our{' '}
            <Link
              to="/terms"
              className="text-primary underline hover:text-primary-hover"
            >
              Terms & Conditions
            </Link>
            .
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            5. Data Sharing
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            We only share data in the following cases:
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed mb-4">
            <li>With members of your invited family household</li>
            <li>
              With service providers that host, secure, operate, or support
              Yaruta, including communications providers used to deliver
              messages you enable
            </li>
            <li>When required by law</li>
            <li>In the event of a merger, acquisition, or asset transfer</li>
          </ul>
          <p className="text-base font-semibold text-foreground">
            We never sell personal data.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            6. Data Retention
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            We retain account data, extracted schedule information, and
            connected communication outputs while your account is active.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            We retain SMS/RCS consent and opt-out records for as long as
            reasonably necessary to document consent, honor messaging
            preferences, comply with legal and carrier requirements, and resolve
            disputes.
          </p>
          <p className="text-base text-foreground leading-relaxed">
            You may request deletion of your data at any time by contacting us
            at{' '}
            <a
              href="mailto:privacy@yaruta.app"
              className="text-primary underline hover:text-primary-hover"
            >
              privacy@yaruta.app
            </a>
            . If you delete your account, your data is removed from active
            systems within approximately 30 days, except where we must retain
            limited information to comply with law, enforce opt-out requests,
            resolve disputes, or protect the service.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            7. Security
          </h2>
          <p className="text-base text-foreground leading-relaxed">
            We use commercially reasonable safeguards to protect your
            information, including encryption in transit and at rest, access
            controls, and least-privilege access for connected services.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            8. Children's Information
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Yaruta is designed for parents and caregivers, not for direct use by
            children.
          </p>
          <p className="text-base text-foreground leading-relaxed">
            Any child-related information entered into Yaruta is provided by a
            parent, guardian, or authorized family household collaborator.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            9. Changes
          </h2>
          <p className="text-base text-foreground leading-relaxed">
            We may update this Privacy Policy as the product evolves. If we make
            material changes, we will update the effective date and provide
            notice as required. Your continued use of Yaruta after an updated
            policy takes effect is subject to the updated policy.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            10. Contact
          </h2>
          <p className="text-base text-foreground leading-relaxed">
            Questions can be sent to:{' '}
            <a
              href="mailto:privacy@yaruta.app"
              className="text-primary underline hover:text-primary-hover"
            >
              privacy@yaruta.app
            </a>
          </p>
        </section>
      </main>

      <footer className="bg-foreground text-muted py-8 px-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img
            src="/logo.svg"
            alt="Yaruta"
            className="h-6 w-6 brightness-0 invert"
          />
          <span className="text-lg font-bold text-white">Yaruta</span>
        </div>
        <div className="flex justify-center gap-6 mb-3">
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground no-underline hover:text-white"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="text-sm text-muted-foreground no-underline hover:text-white"
          >
            Terms & Conditions
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} Yaruta. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
